/**
 * AiReportPage - Consolidated AI analysis report
 * Route: /company/:slug/ai-report
 *
 * Shows all cached AI analyses (organization, departments, teams)
 * in a single scrollable view with collapsible sections.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import companyService from '../../services/companyService';

/* ================================================================== */
/*  Keyframes                                                          */
/* ================================================================== */

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/* ================================================================== */
/*  Styled components                                                  */
/* ================================================================== */

/* ---- Page shell ---- */

const PageContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 48px 24px 80px;
  animation: ${fadeIn} 0.35s ease-out;

  @media (max-width: ${(props) => props.theme.breakpoints?.mobile || '640px'}) {
    padding: 24px 16px 64px;
  }
`;

/* ---- Breadcrumb ---- */

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 24px;
  font-size: 0.82rem;
  color: ${(props) => props.theme.colors.textSecondary};
`;

const BreadcrumbLink = styled(Link)`
  color: ${(props) => props.theme.colors.textSecondary};
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }
`;

const BreadcrumbSep = styled.span`
  color: ${(props) => props.theme.colors.border};
  user-select: none;
`;

const BreadcrumbCurrent = styled.span`
  color: ${(props) => props.theme.colors.text};
  font-weight: 600;
`;

/* ---- Page title ---- */

const PageTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 800;
  color: ${(props) => props.theme.colors.text};
  margin: 0 0 8px 0;
  font-family: ${(props) => props.theme.fonts?.main};
  line-height: 1.2;

  @media (max-width: ${(props) => props.theme.breakpoints?.mobile || '640px'}) {
    font-size: 1.4rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 0.9rem;
  color: ${(props) => props.theme.colors.textSecondary};
  margin: 0 0 32px 0;
  line-height: 1.5;
`;

/* ---- Section (collapsible) ---- */

const SectionWrapper = styled.div`
  margin-bottom: 24px;
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius?.lg || '12px'};
  background: ${(props) => props.theme.colors.bgSecondary};
  overflow: hidden;
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 20px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  text-align: left;

  &:hover {
    background: ${(props) => props.theme.colors.bgTertiary};
  }
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const SectionTitle = styled.h2`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SectionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: ${(props) => props.theme.borderRadius?.full || '9999px'};
  font-size: 0.72rem;
  font-weight: 600;
  background: ${(props) => props.$cached
    ? (props.theme.colors.success || '#10B981') + '20'
    : props.theme.colors.bgTertiary};
  color: ${(props) => props.$cached
    ? (props.theme.colors.success || '#10B981')
    : props.theme.colors.textSecondary};
  flex-shrink: 0;
`;

const SectionLevelBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: ${(props) => props.theme.borderRadius?.full || '9999px'};
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${(props) => props.$bg || props.theme.colors.primaryGlow};
  color: ${(props) => props.$color || props.theme.colors.primary};
  flex-shrink: 0;
`;

const ChevronIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: ${(props) => props.theme.colors.textSecondary};
  transition: transform 0.2s;
  transform: rotate(${(props) => (props.$open ? '180deg' : '0deg')});
  flex-shrink: 0;
`;

const SectionBody = styled.div`
  display: ${(props) => (props.$open ? 'block' : 'none')};
  padding: 0 20px 20px;
`;

/* ---- AI result grid (same 6-card layout as dashboard) ---- */

const AiResultGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: ${(props) => props.theme.breakpoints?.tablet || '768px'}) {
    grid-template-columns: 1fr;
  }
`;

const AiCard = styled.div`
  background: ${(props) => props.theme.colors.bgTertiary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  padding: 16px;
`;

const AiCardTitle = styled.h3`
  font-size: 0.82rem;
  font-weight: 700;
  color: ${(props) => props.$color || props.theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AiList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const AiListItem = styled.li`
  font-size: 0.83rem;
  line-height: 1.6;
  color: ${(props) => props.theme.colors.text};
  padding: 5px 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.border}20;

  &:last-child {
    border-bottom: none;
  }
`;

const AiSummary = styled.div`
  grid-column: 1 / -1;
  background: ${(props) => props.theme.colors.bgTertiary};
  border: 1px solid ${(props) => props.theme.colors.primary}40;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  padding: 16px;
  font-size: 0.88rem;
  line-height: 1.7;
  color: ${(props) => props.theme.colors.text};
`;

const AnalysisDate = styled.span`
  display: block;
  font-size: 0.72rem;
  color: ${(props) => props.theme.colors.textSecondary};
  opacity: 0.7;
  margin-top: 12px;
  text-align: right;
`;

/* ---- Generate button ---- */

const GenerateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  background: linear-gradient(135deg, ${(props) => props.theme.colors.primary}, ${(props) => props.theme.colors.secondary || '#7C3AED'});
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ReGenerateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  background: transparent;
  color: ${(props) => props.theme.colors.textSecondary};
  border: 1px solid ${(props) => props.theme.colors.border};
  font-weight: 500;
  font-size: 0.78rem;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;

  &:hover:not(:disabled) {
    border-color: ${(props) => props.theme.colors.primary};
    color: ${(props) => props.theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GenerateRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  grid-column: 1 / -1;
`;

/* ---- Loading states ---- */

const AiLoadingText = styled.span`
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${rotate} 0.6s linear infinite;
`;

/* ---- Empty / error / no-result states ---- */

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${(props) => props.theme.colors.textSecondary};
  background: ${(props) => props.theme.colors.bgSecondary};
  border: 1px dashed ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius?.lg || '12px'};
  font-size: 0.9rem;
  line-height: 1.6;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 64px 24px;
`;

const ErrorTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  margin: 0 0 12px 0;
`;

const ErrorMessage = styled.p`
  font-size: 0.9rem;
  color: ${(props) => props.theme.colors.textSecondary};
  margin: 0 0 24px 0;
`;

const RetryButton = styled.button`
  padding: 10px 24px;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  background: ${(props) => props.theme.colors.primary};
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: ${(props) => props.theme.colors.secondary};
  }
`;

const ErrorText = styled.p`
  font-size: 0.82rem;
  color: ${(props) => props.theme.colors.error || '#EF4444'};
  margin: 8px 0 0 0;
`;

/* ---- Back link ---- */

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${(props) => props.theme.colors.textSecondary};
  text-decoration: none;
  font-size: 0.85rem;
  margin-top: 32px;
  transition: color 0.2s;

  &:hover {
    color: ${(props) => props.theme.colors.primary};
  }
`;

/* ---- Loading skeleton ---- */

const SkeletonBlock = styled.div`
  background: linear-gradient(
    90deg,
    ${(props) => props.theme.colors.bgTertiary} 25%,
    ${(props) => props.theme.colors.bgSecondary} 50%,
    ${(props) => props.theme.colors.bgTertiary} 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s infinite;
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  height: ${(props) => props.$h || '20px'};
  width: ${(props) => props.$w || '100%'};
  margin-bottom: ${(props) => props.$mb || '0'};
`;

/* ================================================================== */
/*  SVG Icons (inline)                                                 */
/* ================================================================== */

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

/* ================================================================== */
/*  AI Result renderer (reusable for org / dept / team)                */
/* ================================================================== */

function AiResultCards({ result, cachedAt, onRegenerate, generating, t }) {
  if (!result) return null;

  return (
    <AiResultGrid>
      <AiSummary>
        <AiCardTitle>{t('概要', 'Overview')}</AiCardTitle>
        {result.overview || result.summary || t('概要なし', 'No overview available')}
      </AiSummary>

      <AiCard>
        <AiCardTitle $color="#10B981">{t('構造的な強み', 'Structural Strengths')}</AiCardTitle>
        <AiList>
          {(result.structural_strengths || result.strengths || []).map((item, i) => (
            <AiListItem key={i}>{item}</AiListItem>
          ))}
        </AiList>
      </AiCard>

      <AiCard>
        <AiCardTitle $color="#EF4444">{t('構造的なリスク', 'Structural Risks')}</AiCardTitle>
        <AiList>
          {(result.structural_risks || result.challenges || []).map((item, i) => (
            <AiListItem key={i}>{item}</AiListItem>
          ))}
        </AiList>
      </AiCard>

      <AiCard>
        <AiCardTitle $color="#8B5CF6">{t('役職適性', 'Position Fit')}</AiCardTitle>
        <AiList>
          {(result.position_fit || []).map((item, i) => (
            <AiListItem key={i}>{item}</AiListItem>
          ))}
        </AiList>
      </AiCard>

      <AiCard>
        <AiCardTitle $color="#F59E0B">{t('不足している能力', 'Missing Capabilities')}</AiCardTitle>
        <AiList>
          {(result.missing_capabilities || result.missing_roles || []).map((item, i) => (
            <AiListItem key={i}>{item}</AiListItem>
          ))}
        </AiList>
      </AiCard>

      <AiCard>
        <AiCardTitle $color="#06B6D4">{t('配置提案', 'Team Composition Advice')}</AiCardTitle>
        <AiList>
          {(result.team_composition_advice || result.cautions || []).map((item, i) => (
            <AiListItem key={i}>{item}</AiListItem>
          ))}
        </AiList>
      </AiCard>

      <AiCard style={{ gridColumn: '1 / -1' }}>
        <AiCardTitle $color="#6366F1">{t('今すぐ取り組むべきこと', 'Action Items')}</AiCardTitle>
        <AiList>
          {(result.action_items || result.team_building || []).map((item, i) => (
            <AiListItem key={i}>{item}</AiListItem>
          ))}
        </AiList>
      </AiCard>

      <FooterRow>
        {cachedAt && (
          <AnalysisDate style={{ margin: 0 }}>
            {t('分析日時', 'Analyzed')}: {new Date(cachedAt).toLocaleString()}
          </AnalysisDate>
        )}
        <ReGenerateButton onClick={onRegenerate} disabled={generating}>
          {generating ? <><Spinner /> <AiLoadingText>{t('分析中...', 'Analyzing...')}</AiLoadingText></> : t('再分析', 'Re-analyze')}
        </ReGenerateButton>
      </FooterRow>
    </AiResultGrid>
  );
}

/* ================================================================== */
/*  Loading skeleton sub-component                                     */
/* ================================================================== */

function LoadingSkeleton() {
  return (
    <PageContainer>
      <SkeletonBlock $h="16px" $w="240px" $mb="24px" />
      <SkeletonBlock $h="36px" $w="320px" $mb="8px" />
      <SkeletonBlock $h="16px" $w="260px" $mb="32px" />
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ marginBottom: 24 }}>
          <SkeletonBlock $h="56px" $w="100%" $mb="0" />
        </div>
      ))}
    </PageContainer>
  );
}

/* ================================================================== */
/*  Main component                                                     */
/* ================================================================== */

function AiReportPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();

  /* ---- State ---- */
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Organization-level AI
  const [orgAi, setOrgAi] = useState(null);
  const [orgAiCachedAt, setOrgAiCachedAt] = useState(null);
  const [orgAiLoading, setOrgAiLoading] = useState(false);
  const [orgAiError, setOrgAiError] = useState(null);

  // Department-level AI: { [deptId]: { result, cachedAt, loading, error } }
  const [deptAiMap, setDeptAiMap] = useState({});

  // Team-level AI: { [teamId]: { result, cachedAt, loading, error } }
  const [teamAiMap, setTeamAiMap] = useState({});

  // Collapsible sections: { [key]: boolean }
  const [openSections, setOpenSections] = useState({ org: true });

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /* ---- Data fetching ---- */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch company data first to get departments/teams
      const companyData = await companyService.getCompany(slug);
      setCompany(companyData);

      const departments = companyData?.departments || [];
      const allTeams = companyData?.teams || [];

      // Fetch cached AI analyses in parallel
      const [cachedOrg, ...rest] = await Promise.all([
        companyService.getCachedAiAnalysis(slug).catch(() => null),
        ...allTeams.map((team) =>
          companyService.getCachedTeamAiAnalysis(slug, team.id).catch(() => null)
        ),
        ...departments.map((dept) =>
          companyService.getCachedDeptAiAnalysis(slug, dept.id).catch(() => null)
        ),
      ]);

      // Set org AI
      if (cachedOrg && cachedOrg.cached && cachedOrg.result) {
        setOrgAi(cachedOrg.result);
        setOrgAiCachedAt(cachedOrg.created_at);
      }

      // Set team AI map
      const teamResults = rest.slice(0, allTeams.length);
      const newTeamMap = {};
      allTeams.forEach((team, idx) => {
        const cached = teamResults[idx];
        if (cached && cached.cached && cached.result) {
          newTeamMap[team.id] = {
            result: cached.result,
            cachedAt: cached.created_at,
            loading: false,
            error: null,
          };
        } else {
          newTeamMap[team.id] = { result: null, cachedAt: null, loading: false, error: null };
        }
      });
      setTeamAiMap(newTeamMap);

      // Set dept AI map
      const deptResults = rest.slice(allTeams.length);
      const newDeptMap = {};
      departments.forEach((dept, idx) => {
        const cached = deptResults[idx];
        if (cached && cached.cached && cached.result) {
          newDeptMap[dept.id] = {
            result: cached.result,
            cachedAt: cached.created_at,
            loading: false,
            error: null,
          };
        } else {
          newDeptMap[dept.id] = { result: null, cachedAt: null, loading: false, error: null };
        }
      });
      setDeptAiMap(newDeptMap);
    } catch (err) {
      setError(err.message || t('データの取得に失敗しました', 'Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ---- Generate org AI ---- */
  const generateOrgAi = useCallback(async () => {
    setOrgAiLoading(true);
    setOrgAiError(null);
    try {
      const result = await companyService.getAiAnalysis(slug);
      setOrgAi(result);
      setOrgAiCachedAt(new Date().toISOString());
    } catch (err) {
      setOrgAiError(err.message || t('AI分析に失敗しました', 'AI analysis failed'));
    } finally {
      setOrgAiLoading(false);
    }
  }, [slug, t]);

  /* ---- Generate dept AI ---- */
  const generateDeptAi = useCallback(async (deptId) => {
    setDeptAiMap((prev) => ({
      ...prev,
      [deptId]: { ...prev[deptId], loading: true, error: null },
    }));
    try {
      const result = await companyService.getDeptAiAnalysis(slug, deptId);
      setDeptAiMap((prev) => ({
        ...prev,
        [deptId]: {
          result,
          cachedAt: new Date().toISOString(),
          loading: false,
          error: null,
        },
      }));
    } catch (err) {
      setDeptAiMap((prev) => ({
        ...prev,
        [deptId]: {
          ...prev[deptId],
          loading: false,
          error: err.message || t('AI分析に失敗しました', 'AI analysis failed'),
        },
      }));
    }
  }, [slug, t]);

  /* ---- Generate team AI ---- */
  const generateTeamAi = useCallback(async (teamId) => {
    setTeamAiMap((prev) => ({
      ...prev,
      [teamId]: { ...prev[teamId], loading: true, error: null },
    }));
    try {
      const result = await companyService.getTeamAiAnalysis(slug, teamId);
      setTeamAiMap((prev) => ({
        ...prev,
        [teamId]: {
          result,
          cachedAt: new Date().toISOString(),
          loading: false,
          error: null,
        },
      }));
    } catch (err) {
      setTeamAiMap((prev) => ({
        ...prev,
        [teamId]: {
          ...prev[teamId],
          loading: false,
          error: err.message || t('AI分析に失敗しました', 'AI analysis failed'),
        },
      }));
    }
  }, [slug, t]);

  /* ---- Derived ---- */
  const departments = company?.departments || [];
  const allTeams = company?.teams || [];

  /* ================================================================ */
  /*  Render states                                                    */
  /* ================================================================ */

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>{t('ログインが必要です', 'Login Required')}</ErrorTitle>
          <ErrorMessage>
            {t(
              'このページを利用するにはログインまたは新規登録が必要です。',
              'Please log in or sign up to access this page.'
            )}
          </ErrorMessage>
          <RetryButton onClick={() => navigate('/')}>
            {t('ログイン / 新規登録', 'Login / Sign Up')}
          </RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    const isAuthError = error === 'Not authenticated' || error === 'Invalid or expired token';
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorTitle>
            {isAuthError
              ? t('ログインが必要です', 'Login Required')
              : t('エラーが発生しました', 'An Error Occurred')}
          </ErrorTitle>
          <ErrorMessage>
            {isAuthError
              ? t('このページを利用するにはログインまたは新規登録が必要です。', 'Please log in or sign up to access this page.')
              : error}
          </ErrorMessage>
          <RetryButton onClick={isAuthError ? () => navigate('/') : fetchData}>
            {isAuthError
              ? t('ログイン / 新規登録', 'Login / Sign Up')
              : t('再試行', 'Retry')}
          </RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  if (!company) {
    return (
      <PageContainer>
        <EmptyState>
          {t('組織が見つかりませんでした。', 'Organization not found.')}
        </EmptyState>
      </PageContainer>
    );
  }

  /* ================================================================ */
  /*  Main render                                                      */
  /* ================================================================ */

  return (
    <PageContainer>
      {/* ---- Breadcrumb ---- */}
      <Breadcrumb>
        <BreadcrumbLink to={`/company/${slug}`}>{company.name}</BreadcrumbLink>
        <BreadcrumbSep><ChevronRightIcon /></BreadcrumbSep>
        <BreadcrumbCurrent>{t('AI組織レポート', 'AI Organization Report')}</BreadcrumbCurrent>
      </Breadcrumb>

      {/* ---- Page title ---- */}
      <PageTitle>{t('AI組織レポート', 'AI Organization Report')}</PageTitle>
      <PageSubtitle>
        {t(
          '組織・部署・チームのAI分析結果を一覧で確認できます。',
          'View consolidated AI analysis results for your organization, departments, and teams.'
        )}
      </PageSubtitle>

      {/* ================================================================ */}
      {/* Section 1: Organization Overview                                  */}
      {/* ================================================================ */}
      <SectionWrapper>
        <SectionHeader onClick={() => toggleSection('org')}>
          <SectionTitleRow>
            <SectionLevelBadge $bg="rgba(99,102,241,0.15)" $color="#6366F1">
              {t('組織', 'ORG')}
            </SectionLevelBadge>
            <SectionTitle>{company.name}</SectionTitle>
            <SectionBadge $cached={!!orgAi}>
              {orgAi ? t('分析済み', 'Cached') : t('未分析', 'Not analyzed')}
            </SectionBadge>
          </SectionTitleRow>
          <ChevronIcon $open={openSections.org}>
            <ChevronDownIcon />
          </ChevronIcon>
        </SectionHeader>
        <SectionBody $open={openSections.org}>
          {orgAi ? (
            <AiResultCards
              result={orgAi}
              cachedAt={orgAiCachedAt}
              onRegenerate={generateOrgAi}
              generating={orgAiLoading}
              t={t}
            />
          ) : (
            <GenerateRow>
              {orgAiLoading ? (
                <GenerateButton disabled>
                  <Spinner />
                  <AiLoadingText>{t('分析中...', 'Analyzing...')}</AiLoadingText>
                </GenerateButton>
              ) : (
                <GenerateButton onClick={generateOrgAi}>
                  {t('組織全体をAI分析する', 'Analyze Organization with AI')}
                </GenerateButton>
              )}
            </GenerateRow>
          )}
          {orgAiError && <ErrorText>{orgAiError}</ErrorText>}
        </SectionBody>
      </SectionWrapper>

      {/* ================================================================ */}
      {/* Section 2: Departments                                            */}
      {/* ================================================================ */}
      {departments.length > 0 && departments.map((dept) => {
        const key = `dept-${dept.id}`;
        const deptData = deptAiMap[dept.id] || { result: null, cachedAt: null, loading: false, error: null };
        const isOpen = !!openSections[key];

        return (
          <SectionWrapper key={dept.id}>
            <SectionHeader onClick={() => toggleSection(key)}>
              <SectionTitleRow>
                <SectionLevelBadge $bg="rgba(139,92,246,0.15)" $color="#8B5CF6">
                  {t('部署', 'DEPT')}
                </SectionLevelBadge>
                <SectionTitle>{dept.name}</SectionTitle>
                <SectionBadge $cached={!!deptData.result}>
                  {deptData.result ? t('分析済み', 'Cached') : t('未分析', 'Not analyzed')}
                </SectionBadge>
              </SectionTitleRow>
              <ChevronIcon $open={isOpen}>
                <ChevronDownIcon />
              </ChevronIcon>
            </SectionHeader>
            <SectionBody $open={isOpen}>
              {deptData.result ? (
                <AiResultCards
                  result={deptData.result}
                  cachedAt={deptData.cachedAt}
                  onRegenerate={() => generateDeptAi(dept.id)}
                  generating={deptData.loading}
                  t={t}
                />
              ) : (
                <GenerateRow>
                  {deptData.loading ? (
                    <GenerateButton disabled>
                      <Spinner />
                      <AiLoadingText>{t('分析中...', 'Analyzing...')}</AiLoadingText>
                    </GenerateButton>
                  ) : (
                    <GenerateButton onClick={() => generateDeptAi(dept.id)}>
                      {t('部署をAI分析する', 'Analyze Department with AI')}
                    </GenerateButton>
                  )}
                </GenerateRow>
              )}
              {deptData.error && <ErrorText>{deptData.error}</ErrorText>}
            </SectionBody>
          </SectionWrapper>
        );
      })}

      {/* ================================================================ */}
      {/* Section 3: Teams                                                  */}
      {/* ================================================================ */}
      {allTeams.length > 0 && allTeams.map((team) => {
        const key = `team-${team.id}`;
        const teamData = teamAiMap[team.id] || { result: null, cachedAt: null, loading: false, error: null };
        const isOpen = !!openSections[key];

        return (
          <SectionWrapper key={team.id}>
            <SectionHeader onClick={() => toggleSection(key)}>
              <SectionTitleRow>
                <SectionLevelBadge $bg="rgba(16,185,129,0.15)" $color="#10B981">
                  {t('チーム', 'TEAM')}
                </SectionLevelBadge>
                <SectionTitle>{team.name}</SectionTitle>
                <SectionBadge $cached={!!teamData.result}>
                  {teamData.result ? t('分析済み', 'Cached') : t('未分析', 'Not analyzed')}
                </SectionBadge>
              </SectionTitleRow>
              <ChevronIcon $open={isOpen}>
                <ChevronDownIcon />
              </ChevronIcon>
            </SectionHeader>
            <SectionBody $open={isOpen}>
              {teamData.result ? (
                <AiResultCards
                  result={teamData.result}
                  cachedAt={teamData.cachedAt}
                  onRegenerate={() => generateTeamAi(team.id)}
                  generating={teamData.loading}
                  t={t}
                />
              ) : (
                <GenerateRow>
                  {teamData.loading ? (
                    <GenerateButton disabled>
                      <Spinner />
                      <AiLoadingText>{t('分析中...', 'Analyzing...')}</AiLoadingText>
                    </GenerateButton>
                  ) : (
                    <GenerateButton onClick={() => generateTeamAi(team.id)}>
                      {t('チームをAI分析する', 'Analyze Team with AI')}
                    </GenerateButton>
                  )}
                </GenerateRow>
              )}
              {teamData.error && <ErrorText>{teamData.error}</ErrorText>}
            </SectionBody>
          </SectionWrapper>
        );
      })}

      {/* ---- Empty state if no departments or teams ---- */}
      {departments.length === 0 && allTeams.length === 0 && (
        <EmptyState style={{ marginTop: 16 }}>
          {t(
            '部署・チームがまだ登録されていません。設定画面から追加できます。',
            'No departments or teams registered yet. You can add them from the settings page.'
          )}
        </EmptyState>
      )}

      {/* ---- Back link ---- */}
      <BackLink to={`/company/${slug}`}>
        <ArrowLeftIcon />
        {t('ダッシュボードに戻る', 'Back to Dashboard')}
      </BackLink>
    </PageContainer>
  );
}

export default AiReportPage;
